import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { NAV_LINKS, BRAND } from "@/data/urban";
import { NAV } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openDrop, setOpenDrop] = useState(null);
  const { user } = useAuth();
  const location = useLocation();
  const overHero = location.pathname === "/" && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll); onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkColor = overHero ? "text-white/90 hover:text-white" : "text-ink hover:text-ink";

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled || location.pathname !== "/" ? "bg-white/95 backdrop-blur-md hairline-b" : "bg-transparent"}`}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className="flex h-[72px] items-center justify-between">
          <Link to="/" data-testid={NAV.logo} className="leading-tight">
            <div className={`font-heading text-lg sm:text-xl font-extrabold tracking-tight ${overHero ? "text-white" : "text-ink"}`}>URBAN RENOVATIONS</div>
            <div className={`text-[9px] uppercase tracking-[0.24em] ${overHero ? "text-white/70" : "text-ink-soft"}`}>Interiors · Renovation · Design</div>
          </Link>

          <nav className="hidden xl:flex items-center gap-0.5">
            {NAV_LINKS.map((l) => (
              <div key={l.key} className="relative" onMouseEnter={() => l.dropdown && setOpenDrop(l.key)} onMouseLeave={() => setOpenDrop(null)}>
                <Link to={l.href} data-testid={NAV.link(l.key)} className={`inline-flex items-center gap-1 px-3 py-2 rounded-md text-[13px] font-medium transition-colors ${linkColor}`}>
                  {l.label} {l.dropdown && <ChevronDown className="h-3.5 w-3.5" />}
                </Link>
                <AnimatePresence>
                  {l.dropdown && openDrop === l.key && (
                    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}
                      className="absolute left-0 top-full pt-2 min-w-[240px]">
                      <div className="bg-white border border-hairline rounded-xl shadow-xl overflow-hidden py-2">
                        {l.dropdown.map((d) => (
                          <Link key={d.href} to={d.href} className="block px-4 py-2.5 text-sm text-ink hover:bg-canvas hover:text-black transition-colors">{d.label}</Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a href={`tel:${BRAND.phoneRaw}`} className={`hidden md:inline-flex items-center gap-1.5 text-xs font-semibold ${overHero ? "text-white" : "text-ink"}`}>
              <Phone className="h-3.5 w-3.5" /> {BRAND.phone}
            </a>
            <Link to="/contact" data-testid={NAV.quoteBtn} className="hidden sm:inline-flex items-center gap-2 bg-black text-white px-5 py-2.5 rounded-md text-[12px] font-bold uppercase tracking-wider hover:bg-ink transition-colors">
              Get Free Quote
            </Link>
            {user ? (
              <Link to="/admin" data-testid="nav-admin" className={`hidden sm:inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-[12px] font-semibold ${overHero ? "border-white/40 text-white hover:bg-white/10" : "border-hairline text-ink hover:border-ink"}`}>Admin</Link>
            ) : (
              <Link to="/login" data-testid="nav-signin" className={`hidden sm:inline-flex items-center rounded-md border px-3 py-2 text-[12px] font-semibold ${overHero ? "border-white/40 text-white hover:bg-white/10" : "border-hairline text-ink hover:border-ink"}`}>Sign in</Link>
            )}
            <button type="button" data-testid={NAV.menuToggle} onClick={() => setOpen((v) => !v)} aria-label="Menu" className={`xl:hidden h-10 w-10 rounded-md flex items-center justify-center ${overHero ? "text-white bg-white/10" : "text-ink bg-canvas"}`}>{open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}</button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="xl:hidden overflow-hidden bg-white border-t border-hairline">
            <div className="flex flex-col px-5 py-2 max-h-[80vh] overflow-y-auto">
              {NAV_LINKS.map((l) => (
                <Link key={l.key} to={l.href} onClick={() => setOpen(false)} data-testid={NAV.mobileLink(l.key)} className="py-3 text-ink font-medium border-b border-hairline last:border-0">{l.label}</Link>
              ))}
              <Link to="/contact" onClick={() => setOpen(false)} className="mt-3 mb-2 inline-flex items-center justify-center gap-2 bg-black text-white py-3 rounded-md font-bold uppercase tracking-wider text-sm">Get Free Quote</Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
